import React from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Paper,
  Stack,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Stop,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import { useDrawer } from '../contexts/DrawerContext';
import { getContrastText } from '../utils/colorUtils';

export default function MiniPlayer({ customColors }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const { drawerOpen, drawerWidth } = useDrawer();
  const {
    isPlaying,
    currentTrack,
    volume,
    progress,
    duration,
    handlePlay,
    handlePause,
    handleStop,
    handleVolumeChange,
    handleProgressChange,
    isPlayerVisible,
    togglePlayerVisibility
  } = usePlayer();

  const [isMuted, setIsMuted] = React.useState(false);
  const [previousVolume, setPreviousVolume] = React.useState(1);

  if (!currentUser || !currentTrack || !currentTrack.title) return null;

  const handleVolumeToggle = () => {
    if (isMuted) {
      handleVolumeChange(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      handleVolumeChange(0);
      setIsMuted(true);
    }
  };

  const handleVolumeSliderChange = (event, newValue) => {
    handleVolumeChange(newValue);
    if (newValue === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const textColor = customColors?.primary ? getContrastText(customColors.primary) : 'white';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: drawerOpen ? `${drawerWidth}px` : 0 },
          right: 0,
          width: { 
            xs: 'calc(100% - 17px)', // Deixa espaço para a barra de rolagem
            sm: drawerOpen ? `calc(100% - ${drawerWidth}px - 17px)` : 'calc(100% - 17px)'
          },
          zIndex: 1000,
          transition: theme.transitions.create(['left', 'width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard * 1.5,
          }),
        }}
      >
        {/* Botão de ocultar/mostrar - Fora do Collapse para garantir que sempre funcione */}
        <Tooltip title={isPlayerVisible ? "Ocultar player" : "Mostrar player"}>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePlayerVisibility();
            }}
            sx={{
              position: 'absolute',
              bottom: isPlayerVisible ? '100%' : 0, // Colado ao player quando visível
              right: 0, // Alinhado com a borda direita do player
              bgcolor: customColors?.primary || '#1B365D',
              color: textColor,
              zIndex: 1002, // Z-index muito alto para garantir que está acima de tudo
              '&:hover': {
                bgcolor: customColors?.primary || '#1B365D',
                opacity: 0.9
              },
              width: 28,
              height: 28,
              borderRadius: isPlayerVisible ? '4px 4px 0 0' : '0',
              pointerEvents: 'auto',
              transition: theme.transitions.create(['bottom'], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard * 1.5,
              }),
              '& .MuiSvgIcon-root': {
                fontSize: 20
              }
            }}
          >
            {isPlayerVisible ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Tooltip>
        <Collapse in={isPlayerVisible}>
        <Paper
          elevation={3}
          sx={{
        width: '100%',
        p: 1,
        bgcolor: customColors?.primary || '#1B365D',
        color: textColor,
        display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
            gap: { xs: 1, sm: 2 }
      }}
    >
      <Typography 
        variant="body2" 
        noWrap 
        sx={{ 
              width: { xs: '100%', sm: '200px' },
              maxWidth: { xs: '100%', sm: '300px' },
              color: textColor,
              textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        {currentTrack.title || 'Reproduzindo...'}
      </Typography>

      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%',
        mx: { xs: 0, sm: 2 }
      }}>
        <Slider
          value={currentTrack.videoId ? (progress / duration) * 100 : progress}
          onChange={handleProgressChange}
          min={0}
          max={currentTrack.videoId ? 100 : duration}
          aria-label="Progresso"
          sx={{
            color: customColors?.secondary || '#78B6CB',
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              color: textColor,
            },
            '& .MuiSlider-track': {
              height: 4,
              color: textColor,
            },
            '& .MuiSlider-rail': {
              height: 4,
              opacity: 0.5,
              color: textColor,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: textColor }}>
            {formatTime(progress)}
          </Typography>
          <Typography variant="caption" sx={{ color: textColor }}>
            {formatTime(duration)}
          </Typography>
        </Box>
      </Box>

      <Stack 
        direction="row" 
        spacing={1} 
        alignItems="center"
        sx={{
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}
      >
        <IconButton
          onClick={isPlaying ? handlePause : handlePlay}
          sx={{ color: textColor }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <IconButton
          onClick={handleStop}
          sx={{ color: textColor }}
        >
          <Stop />
        </IconButton>

        <IconButton
          onClick={handleVolumeToggle}
          sx={{ color: textColor }}
        >
          {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
        </IconButton>
        
        <Slider
          value={volume}
          onChange={handleVolumeSliderChange}
          aria-label="Volume"
          min={0}
          max={1}
          step={0.1}
          sx={{
                width: { xs: 80, sm: 100 },
            color: customColors?.secondary || '#78B6CB',
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              color: textColor,
            },
            '& .MuiSlider-track': {
              height: 4,
              color: textColor,
            },
            '& .MuiSlider-rail': {
              height: 4,
              opacity: 0.5,
              color: textColor,
            },
          }}
        />
      </Stack>
    </Paper>
        </Collapse>
      </Box>
    </>
  );
} 